import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase/firebase';
import '../components/ProjectDashboardNotif.css';
import Sidebar from './Sidebar';
import InviteNotification from './InviteNotification';
import { Outlet } from 'react-router-dom';

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const auth = getAuth();
  const user = auth.currentUser;
  const { initialized, userData } = useAuth();
  const hasRedirectedRef = useRef(false);
  const [company, setCompany] = useState(null);
  const [companyLoaded, setCompanyLoaded] = useState(false);

  // Safety check: If user just logged in and is on a project route, redirect to /home
  // This prevents cross-account redirects from browser history
  useEffect(() => {
    if (initialized && user && !hasRedirectedRef.current) {
      // Check if we're on a project route
      if (location.pathname.startsWith('/project/')) {
        // Check if this is a fresh login (no sessionStorage flag)
        const hasVisitedHome = sessionStorage.getItem('hasVisitedHome');
        if (!hasVisitedHome) {
          console.log('⚠️ User on project route after login, redirecting to /home for safety');
          sessionStorage.setItem('hasVisitedHome', 'true');
          hasRedirectedRef.current = true;
          navigate('/home', { replace: true });
          return;
        }
      } else if (location.pathname === '/home') {
        // Mark that user has visited home
        sessionStorage.setItem('hasVisitedHome', 'true');
      }
    }
  }, [initialized, user, location.pathname, navigate]);

  // Load company for global billing banner (if user is a PM in a company)
  useEffect(() => {
    const loadCompany = async () => {
      if (!user || !userData?.companyId) {
        setCompany(null);
        setCompanyLoaded(true);
        return;
      }
      try {
        const companyRef = doc(db, 'companies', userData.companyId);
        const snap = await getDoc(companyRef);
        if (snap.exists()) {
          setCompany(snap.data());
        } else {
          setCompany(null);
        }
      } catch (e) {
        console.error('Failed to load company for layout banner', e);
        setCompany(null);
      } finally {
        setCompanyLoaded(true);
      }
    };

    loadCompany();
  }, [user, userData?.companyId]);

  const showBillingBanner = !!company && !company.billingManaged && (() => {
    const status = company.subscriptionStatus || 'active';
    const needsPayment = !!company.paymentNeedsAttention;
    return needsPayment || status === 'canceled' || status === 'past_due';
  })();

  return (
    <div style={{
      display: 'flex',
      minHeight: '100vh',
      height: '100vh',
      background: 'var(--branding-app-surface)'
    }}>
      <Sidebar />
      <div style={{
        flex: 1,
        minHeight: '100vh',
        height: '100vh',
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        backgroundColor: 'var(--branding-app-surface)',
        overscrollBehavior: 'contain'
      }}>
        {/* Invite Notification - Fixed Position */}
        {user && (
          <div style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 1000
          }}>
            <InviteNotification />
          </div>
        )}
        {user && companyLoaded && showBillingBanner && (
          <div style={{
            margin: '16px 24px 0 24px',
            padding: '10px 14px',
            borderRadius: 10,
            background: company.subscriptionStatus === 'canceled' ? '#422006' : '#431f1f',
            color: '#fef3c7',
            fontSize: 13,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
          }}>
            <div>
              {company.paymentNeedsAttention && (
                <div>Update your payment method to avoid service interruption.</div>
              )}
              {!company.paymentNeedsAttention && company.subscriptionStatus === 'past_due' && (
                <div>Your payment is past due. Please update your payment method.</div>
              )}
              {company.subscriptionStatus === 'canceled' && (
                <div>Your subscription has been canceled. Re-subscribe to add new PMs or manage billing.</div>
              )}
            </div>
          </div>
        )}
        <Outlet />
      </div>
    </div>
  );
};

export default Layout;
